import { useState } from "react";
import { Loader2, Mail, Pause, Play, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const box = "rounded-sm border border-[var(--line)] p-5";
const btn = "inline-flex h-10 items-center gap-2 rounded-full border border-[var(--line)] px-4 text-xs font-semibold uppercase tracking-[0.14em] transition-colors hover:border-[var(--gold)] disabled:opacity-40";

/** Compteurs en temps réel de la campagne. */
export function ProspectStats({ s }) {
  const items = [
    ["total", "Prospects", s.total], ["verified", "Adresses vérifiées", s.verified], ["sent", "Emails envoyés", s.sent],
    ["delivered", "Acceptés par le serveur", s.delivered], ["rejected", "Rejetés", s.rejected], ["followups", "Relances", s.followups],
    ["clicks", "Clics vers la formation", s.clicks], ["replies", "Réponses", s.replies], ["interested", "Intéressés", s.interested],
    ["info", "Demandes d'infos", s.info], ["enrolled", "Inscriptions", s.enrolled], ["revenue", "Chiffre d'affaires", `${(s.revenue || 0).toFixed(2).replace(".", ",")} €`],
  ];
  return (
    <div data-testid="prospect-stats" className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
      {items.map(([k, l, v]) => (
        <div key={k} data-testid={`prospect-stat-${k}`} className="rounded-sm bg-[var(--ink-2)] p-3">
          <p className="text-xs uppercase tracking-[0.12em] text-[var(--parchment)]/75">{l}</p>
          <p className="mt-1 font-display text-2xl">{v ?? 0}</p>
        </div>
      ))}
    </div>
  );
}

/** Tests d'objets : envois, clics, réponses et inscriptions par variante. */
export function VariantTable({ variants }) {
  const rows = Object.entries(variants || {});
  if (!rows.length) return <p className="text-sm text-[var(--parchment)]/80">Les résultats par objet apparaîtront après les premiers envois.</p>;
  return (
    <table data-testid="prospect-variants" className="w-full text-left text-sm">
      <thead className="text-xs uppercase tracking-[0.12em] text-[var(--parchment)]/75"><tr><th className="py-2">Objet</th><th>Envoyés</th><th>Clics</th><th>Réponses</th><th>Inscriptions</th></tr></thead>
      <tbody>{rows.map(([k, v]) => <tr key={k} className="border-t border-[var(--line)]"><td className="py-2 pr-3">{k} · {v.subject}</td><td>{v.sent}</td><td>{v.clicks}</td><td>{v.replies}</td><td>{v.enrolled}</td></tr>)}</tbody>
    </table>
  );
}

/** Validation, pause / reprise et envoi d'exemples. Rien ne part sans « JE VALIDE ». */
export function CampaignControl({ campaign, onDone }) {
  const [confirm, setConfirm] = useState("");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState("");
  const act = async (action) => {
    setBusy(action);
    try { await api.post("/admin/prospects-campaign", { action, confirm }); toast.success(action === "approve" ? "Campagne validée : premiers envois au prochain créneau de 10 h (jours ouvrés)" : action === "pause" ? "Campagne en pause" : "Campagne reprise"); onDone(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const test = async () => {
    setBusy("test");
    try { await api.post("/admin/prospects-test", { email: to }); toast.success("3 exemples remis au fournisseur (salon, créatrice, relance)"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const state = !campaign?.approved ? "En attente de votre validation : aucun email ne part." : campaign.paused ? "En pause : aucun email ne part." : "Active : 10 nouveaux prospects par jour ouvré à partir de 10 h, relance unique à J+5.";
  return (
    <section data-testid="prospect-campaign" className={box}>
      <p data-testid="prospect-campaign-state" className="flex items-center gap-2 text-sm"><ShieldCheck className="h-4 w-4 text-[var(--gold-soft)]" />{state}</p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        {!campaign?.approved ? (
          <>
            <input data-testid="prospect-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Tapez JE VALIDE" className="h-10 rounded-sm border border-[var(--line)] bg-transparent px-3 text-sm" />
            <button data-testid="prospect-approve-btn" disabled={confirm !== "JE VALIDE" || !!busy} onClick={() => act("approve")} className={btn}>{busy === "approve" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Autoriser les envois</button>
          </>
        ) : campaign.paused ? (
          <button data-testid="prospect-resume-btn" disabled={!!busy} onClick={() => act("resume")} className={btn}><Play className="h-4 w-4" /> Reprendre</button>
        ) : (
          <button data-testid="prospect-pause-btn" disabled={!!busy} onClick={() => act("pause")} className={btn}><Pause className="h-4 w-4" /> Mettre en pause</button>
        )}
        <span className="mx-2 hidden h-6 w-px bg-[var(--line)] sm:block" />
        <input data-testid="prospect-test-email" type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="Votre email pour les exemples" className="h-10 rounded-sm border border-[var(--line)] bg-transparent px-3 text-sm" />
        <button data-testid="prospect-test-btn" disabled={!to || !!busy} onClick={test} className={btn}>{busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} M'envoyer les exemples</button>
      </div>
    </section>
  );
}

/** Aperçu exact des emails (objets A/B, relance). */
export function EmailPreviews() {
  const [v, setV] = useState("A1");
  const [data, setData] = useState(null);
  const load = async (k) => {
    setV(k);
    try { setData((await api.get(`/admin/prospects-preview/${k}`)).data); } catch (e) { toast.error(errMsg(e)); }
  };
  const labels = { A1: "Salons · objet 1", A2: "Salons · objet 2", B1: "Créatrices · objet 1", B2: "Créatrices · objet 2", F: "Relance J+5" };
  return (
    <section data-testid="prospect-previews" className={box}>
      <div className="flex flex-wrap gap-2">
        {Object.entries(labels).map(([k, l]) => <button key={k} data-testid={`prospect-preview-${k}`} onClick={() => load(k)} className={`${btn} ${v === k && data ? "border-[var(--gold)]" : ""}`}>{l}</button>)}
      </div>
      {data ? (
        <div className="mt-4">
          <p data-testid="prospect-preview-subject" className="text-sm">Objet : <b>{data.subject}</b> <span className="text-[var(--parchment)]/75">(exemple pour {data.to})</span></p>
          <iframe title="Aperçu email" data-testid="prospect-preview-frame" srcDoc={data.html} className="mt-3 h-[640px] w-full rounded-sm bg-white" sandbox="" />
        </div>
      ) : <p className="mt-3 text-sm text-[var(--parchment)]/80">Choisissez un email pour l'afficher tel qu'il sera reçu.</p>}
    </section>
  );
}
